/**
 * Централизованный файл переводов констант для админ-панели
 * Все переводы на русский язык
 */

// ============================================
// Периоды времени
// ============================================
export const PERIOD_LABELS = {
  week: 'Неделя',
  month: 'Месяц',
  year: 'Год',
  custom: 'Произвольный',
} as const;

export const PERIOD_LABELS_GENITIVE = {
  week: 'неделю',
  month: 'месяц',
  year: 'год',
  custom: 'период',
} as const;

// ============================================
// Метрики Dashboard
// ============================================
export const METRIC_LABELS = {
  totalUsers: 'Всего пользователей',
  activeUsers: 'Активных пользователей',
  newUsersToday: 'Новых за сегодня',
  newUsersInPeriod: 'Новых за период',
  activeSubscriptions: 'Активных подписок',
  totalSubscriptions: 'Всего подписок',
  onlineUsers: 'Онлайн',
  onlineUsersCount: 'Пользователей онлайн',
  totalRevenue: 'Общая выручка',
  confirmedRevenue: 'Подтвержденная выручка',
  pendingRevenue: 'Ожидает подтверждения',
  totalCourses: 'Всего курсов',
  publishedCourses: 'Опубликовано курсов',
  revenueChange: 'Изменение выручки',
  conversionRate: 'Конверсия',
} as const;

// ============================================
// Статусы заказов (OrderStatus)
// ============================================
export const ORDER_STATUS_LABELS = {
  DRAFT: 'Черновик',
  PENDING: 'Ожидает',
  PROCESSING: 'В обработке',
  PAID: 'Оплачен',
  CONFIRMED: 'Подтвержден',
  CANCELLED: 'Отменен',
  REFUNDED: 'Возврат',
  FAILED: 'Ошибка',
} as const;

export const ORDER_STATUS_COLORS = {
  DRAFT: '#9ca3af',    // gray-400
  PENDING: '#f59e0b',  // amber-500
  PROCESSING: '#3b82f6', // blue-500
  PAID: '#22c55e',     // green-500
  CONFIRMED: '#10b981', // emerald-500
  CANCELLED: '#ef4444', // red-500
  REFUNDED: '#8b5cf6', // violet-500
  FAILED: '#dc2626',   // red-600
} as const;

// ============================================
// Статусы платежей (PaymentStatus)
// ============================================
export const PAYMENT_STATUS_LABELS = {
  PENDING: 'Ожидание',
  CONFIRMED: 'Подтвержден',
  FAILED: 'Отклонен',
  REFUNDED: 'Возврат',
  EXPIRED: 'Истек',
} as const;

export const PAYMENT_STATUS_COLORS = {
  PENDING: '#f59e0b',  // amber-500
  CONFIRMED: '#22c55e', // green-500
  FAILED: '#ef4444',   // red-500
  REFUNDED: '#8b5cf6', // violet-500
  EXPIRED: '#6b7280',  // gray-500
} as const;

// ============================================
// Статусы подписок (SubscriptionStatus)
// ============================================
export const SUBSCRIPTION_STATUS_LABELS = {
  ACTIVE: 'Активна',
  PAUSED: 'Приостановлена',
  CANCELLED: 'Отменена',
  EXPIRED: 'Истекла',
  TRIAL: 'Пробный период',
} as const;

// ============================================
// Типы товаров (ProductType / OrderTargetType)
// ============================================
export const PRODUCT_TYPE_LABELS = {
  COURSE: 'Курс',
  CHANNEL_PASS: 'Доступ к каналу',
  SUBSCRIPTION_PLAN: 'Абонемент',
  EVENT: 'Мероприятие',
} as const;

// ============================================
// Страны (Geography)
// ============================================
export const COUNTRY_NAMES: Record<string, string> = {
  RU: 'Россия',
  UZ: 'Узбекистан',
  KZ: 'Казахстан',
  BY: 'Беларусь',
  UA: 'Украина',
  US: 'США',
  GB: 'Великобритания',
  DE: 'Германия',
  FR: 'Франция',
  TR: 'Турция',
  AE: 'ОАЭ',
  KG: 'Кыргызстан',
  TJ: 'Таджикистан',
  TM: 'Туркменистан',
  AZ: 'Азербайджан',
  AM: 'Армения',
  GE: 'Грузия',
  MD: 'Молдова',
  LV: 'Латвия',
  LT: 'Литва',
  EE: 'Эстония',
  PL: 'Польша',
  CZ: 'Чехия',
  IT: 'Италия',
  ES: 'Испания',
  IL: 'Израиль',
  CN: 'Китай',
  IN: 'Индия',
  JP: 'Япония',
  KR: 'Южная Корея',
};

// ============================================
// Валюты
// ============================================
export const CURRENCY_LABELS: Record<string, string> = {
  USD: 'Доллар США',
  UZS: 'Узбекский сум',
  RUB: 'Российский рубль',
  EUR: 'Евро',
  KZT: 'Казахстанский тенге',
  UAH: 'Украинская гривна',
  BYN: 'Белорусский рубль',
  GBP: 'Фунт стерлингов',
};

export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  UZS: 'сум',
  RUB: '₽',
  EUR: '€',
  KZT: '₸',
  UAH: '₴',
  BYN: 'Br',
  GBP: '£',
};

// ============================================
// Действия сотрудников (OrderHistoryAction)
// ============================================
export const STAFF_ACTION_LABELS = {
  STATUS_CHANGED: 'Смена статуса',
  NOTE_ADDED: 'Добавлена заметка',
  NOTE_UPDATED: 'Обновлена заметка',
  NOTE_DELETED: 'Удалена заметка',
  NOTE_PINNED: 'Заметка закреплена',
  NOTE_UNPINNED: 'Заметка откреплена',
  CHAT_LINKED: 'Привязан чат',
  CHAT_UNLINKED: 'Отвязан чат',
  ITEM_ADDED: 'Добавлен товар',
  ITEM_REMOVED: 'Удален товар',
  ITEM_PRICE_CHANGED: 'Изменена цена позиции',
  ASSIGNED: 'Назначен ответственный',
  UNASSIGNED: 'Снят ответственный',
  CREATED: 'Создан',
  PAYMENT_CREATED: 'Создан платёж',
  PAYMENT_CONFIRMED: 'Платёж подтверждён',
  PAYMENT_REJECTED: 'Платёж отклонён',
  PAYMENT_REFUNDED: 'Платёж возвращён',
  BONUS_VALUE_CHANGED: 'Изменено значение привилегии',
  PAYMENT_LINK_CREATED: 'Создана платёжная ссылка',
  PAYMENT_LINK_OPENED: 'Открыта платёжная ссылка',
  PAYMENT_ATTEMPT_FAILED: 'Неуспешная попытка оплаты',
  ORDER_VIEWED_BY_USER: 'Просмотр заказа клиентом',
} as const;

// ============================================
// Провайдеры платежей
// ============================================
export const PAYMENT_PROVIDER_LABELS = {
  CLICK: 'Click',
  PAYME: 'Payme',
  UZUM: 'Uzum',
  STRIPE: 'Stripe',
  MANUAL: 'Вручную',
  TELEGRAM_STARS: 'Telegram Stars',
} as const;

// ============================================
// Типы событий (единый словарь для triggers)
// ============================================
export const EVENT_TYPE_LABELS: Record<string, string> = {
  // Подписки
  SUBSCRIPTION_PURCHASED: 'Покупка подписки',
  SUBSCRIPTION_CREATED: 'Создание подписки',
  SUBSCRIPTION_STARTED: 'Активация подписки',
  SUBSCRIPTION_EXPIRING: 'Подписка скоро истечёт',
  SUBSCRIPTION_EXPIRING_SOON: 'Подписка скоро истечёт',
  SUBSCRIPTION_EXPIRATION: 'Дата истечения подписки',
  SUBSCRIPTION_EXPIRED: 'Подписка истекла',
  // Заказы
  ORDER_CREATED: 'Создан заказ',
  ORDER_PAID: 'Заказ оплачен',
  ORDER_CANCELLED: 'Заказ отменён',
  // Курсы
  COURSE_COMPLETED: 'Курс завершён',
  COURSE_AVAILABLE: 'Курс доступен',
  NEW_COURSE_AVAILABLE: 'Новый курс доступен',
  COURSE_ACCESS_EXPIRED: 'Доступ к курсу истёк',
  COURSE_DRIP_RELEASED: 'Материал курса разблокирован',
  NEW_LESSON: 'Новый урок',
  // Домашнее задание
  HOMEWORK_SUBMITTED: 'Домашнее задание отправлено',
  HOMEWORK_REMINDER: 'Напоминание о задании',
  // Пользователь
  USER_REGISTERED: 'Регистрация пользователя',
  // Каналы
  CHANNEL_PASS_LIMIT_REACHED: 'Исчерпан лимит доступа к каналу',
  CHANNEL_PASS_EXPIRED: 'Доступ к каналу истёк',
  // Семейная подписка
  FAMILY_MEMBER_ADDED: 'Добавлен член семьи',
  FAMILY_MEMBER_REMOVED: 'Удалён член семьи',
  FAMILY_SLOTS_CHANGED: 'Изменено кол-во слотов семьи',
  FAMILY_SLOTS_INCREASED: 'Слоты семьи увеличены',
  FAMILY_SLOTS_DECREASED: 'Слоты семьи уменьшены',
  // Доступы
  ACCESS_GRANTED: 'Выдан доступ',
  // Системные
  PAYMENT_REMINDER: 'Напоминание об оплате',
  REMINDER: 'Напоминание',
  BROADCAST: 'Рассылка',
  SYSTEM: 'Системное',
  MARKETING: 'Маркетинг',
  CUSTOM: 'Произвольное',
};

/** @deprecated Используйте EVENT_TYPE_LABELS */
export const NOTIFICATION_TYPE_LABELS = EVENT_TYPE_LABELS;

// ============================================
// Роли пользователей
// ============================================
export const ROLE_LABELS = {
  SUPER_ADMIN: 'Супер-администратор',
  ADMIN: 'Администратор',
  MANAGER: 'Менеджер',
  SUPPORT: 'Поддержка',
  MODERATOR: 'Модератор',
  CONTENT_MANAGER: 'Контент-менеджер',
} as const;

// ============================================
// Типы курсов
// ============================================
export const COURSE_TYPE_LABELS = {
  ONLINE: 'Онлайн-курс',
  MEETING: 'Клуб',
} as const;

// ============================================
// Статусы домашнего задания
// ============================================
export const HOMEWORK_STATUS_LABELS = {
  NOT_STARTED: 'Не начато',
  IN_PROGRESS: 'В процессе',
  SUBMITTED: 'Отправлено',
  APPROVED: 'Принято',
  REJECTED: 'Отклонено',
} as const;

// ============================================
// Статусы связи пользователь ↔ Telegram-бот (UserBotLinkStatus)
// ============================================
export const USER_BOT_LINK_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активен',
  BLOCKED: 'Заблокировал',
};

// ============================================
// Helper функции
// ============================================

/**
 * Получить название страны по коду
 */
export function getCountryName(code: string): string {
  return COUNTRY_NAMES[code] ?? code;
}

/**
 * Получить метку статуса связи пользователь ↔ Telegram-бот
 */
export function getUserBotLinkStatusLabel(status: string): string {
  return USER_BOT_LINK_STATUS_LABELS[status] ?? status;
}

/**
 * Получить метку статуса заказа
 */
export function getOrderStatusLabel(status: string): string {
  return ORDER_STATUS_LABELS[status as keyof typeof ORDER_STATUS_LABELS] ?? status;
}

/**
 * Получить метку статуса платежа
 */
export function getPaymentStatusLabel(status: string): string {
  return PAYMENT_STATUS_LABELS[status as keyof typeof PAYMENT_STATUS_LABELS] ?? status;
}

/**
 * Получить русскую метку типа события
 */
export function getEventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

/**
 * Получить метку периода
 */
export function getPeriodLabel(period: string): string {
  return PERIOD_LABELS[period as keyof typeof PERIOD_LABELS] ?? period;
}

/**
 * Получить метку действия сотрудника
 */
export function getStaffActionLabel(action: string): string {
  return STAFF_ACTION_LABELS[action as keyof typeof STAFF_ACTION_LABELS] ?? action;
}

/**
 * Форматировать сумму с валютой
 */
export function formatCurrencyAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: currency === 'UZS' ? 0 : 2,
  }).format(amount);
}

/**
 * Форматировать число
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU').format(value);
}

/**
 * Форматировать процент
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}
